import { useState, useCallback } from 'react';
import { useAppLock } from '@/contexts/AppLockContext';
import useBiometricAuth from '@/hooks/useBiometricAuth';

/**
 * useSensitiveAction - Hook for protecting sensitive actions with biometric auth
 * 
 * Use this hook when you need to protect specific actions like:
 * - Viewing emergency summary
 * - Exporting PDF
 * - Sharing via QR code
 * - Viewing medical alert screen
 * 
 * The hook will:
 * 1. Check if sensitive action protection is enabled
 * 2. Check if user recently verified (within 1 minute)
 * 3. If not, trigger biometric authentication
 * 4. Execute the action only after successful auth
 */
const useSensitiveAction = () => {
  const { 
    appLockEnabled, 
    requireForSensitive,
    requiresSensitiveUnlock,
    recordSensitiveUnlock
  } = useAppLock();
  
  const { isSupported, authenticate } = useBiometricAuth();
  
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Execute a sensitive action with biometric protection
   * @param {Function} action - The action to execute after verification
   * @param {Object} options - Optional settings
   * @param {string} options.actionName - Name of the action (for error messages)
   * @param {boolean} options.skipIfUnsupported - Skip auth if biometric not supported (default: true)
   * @returns {Promise<boolean>} - True if action was executed
   */
  const executeSensitiveAction = useCallback(async (action, options = {}) => {
    const { 
      actionName = 'this action',
      skipIfUnsupported = true 
    } = options;
    
    setError(null);

    // If app lock is disabled or sensitive protection is off, execute immediately
    if (!appLockEnabled || !requireForSensitive) {
      await action();
      return true;
    }

    // If biometric not supported and we should skip, execute immediately
    if (!isSupported && skipIfUnsupported) {
      await action();
      return true;
    }

    // Check if we need to verify
    if (!requiresSensitiveUnlock()) {
      // Recently verified, execute immediately
      await action();
      return true;
    }

    // Need to verify
    setIsVerifying(true);

    try {
      const verified = await authenticate();
      
      if (verified) {
        recordSensitiveUnlock();
        await action();
        return true;
      } else {
        setError(`Verification required for ${actionName}`);
        return false;
      }
    } catch (err) {
      console.error('Sensitive action verification failed:', err);
      
      if (err.name === 'NotAllowedError') {
        setError('Authentication was cancelled');
      } else {
        setError(err.message || `Failed to verify for ${actionName}`);
      }
      
      return false;
    } finally {
      setIsVerifying(false);
    }
  }, [appLockEnabled, requireForSensitive, isSupported, requiresSensitiveUnlock, authenticate, recordSensitiveUnlock]);

  /**
   * Check if action will require verification (without triggering it)
   */
  const willRequireVerification = useCallback(() => {
    return appLockEnabled && requireForSensitive && requiresSensitiveUnlock();
  }, [appLockEnabled, requireForSensitive, requiresSensitiveUnlock]);

  return {
    executeSensitiveAction,
    willRequireVerification,
    isVerifying,
    error,
    clearError: () => setError(null),
  };
};

export default useSensitiveAction;
